<template>
  <div class="container-lg booking-container p-md-4">
    <div
      v-if="loggedInUser"
      class="
        card
        theme-card
        mb-2
        px-md-4 px-3
        py-3
        d-flex
        flex-row
        align-items-center
      "
    >
      <button class="btn px-2 mr-3" @click.prevent="goBack()">
        <i class="fas fa-arrow-left"></i>
      </button>
      <img
        :src="userAvatar"
        class="rounded-circle mr-3 user-avatar-img"
        width="50"
        height="50"
        alt="User Profile Picture"
      />
      <div class="booking-container__user-name">
        <span class="font-weight-normal">Welcome, </span>
        {{ loggedInUser.first }}
      </div>
    </div>
    <alert
      v-if="settingsLoading || bookingProviderLoading"
      class="py-8-px light-shadow"
    ></alert>
    <div
      v-else-if="!onlineBookingEnabled"
      class="card theme-card booking-container__header px-md-4 px-3 py-3"
    >
      <div class="booking-container__title">
        Booking Temporarily Unavailable
      </div>
      <div class="booking-container__subtitle">
        We&apos;re taking a short break to improve your booking experience. Our
        service booking portal is currently unavailable, but don&apos;t worry
        &dash; we&apos;re working hard to get it back online. Please check back
        soon, and thank you for your patience as we enhance your scheduling
        experience!
      </div>
    </div>
    <div
      v-else-if="providerId && !bookingProvider"
      class="
        card
        theme-card
        booking-container__header
        px-md-4 px-3
        py-3
        text-center
      "
    >
      <div class="booking-container__title">
        {{ $filters.providerLabel("singular", true) }} Not Found
      </div>
    </div>
    <div
      v-else-if="bookingProvider && !bookingProvider.enable_online_booking"
      class="card theme-card booking-container__header px-md-4 px-3 py-3"
    >
      <div class="booking-container__title">
        Online booking unavailable for this
        {{ $filters.providerLabel("singular", false) }}
      </div>
      <div class="booking-container__subtitle">
        Unfortunately, this {{ $filters.providerLabel("singular", false) }} is
        not currently accepting online appointments. Please check back later, go
        to our <a href="/booking">public booking page</a>, or contact our
        <a href="mailto:info@communitycareconnection.com">support team</a> for
        assistance.
      </div>
    </div>
    <div v-else>
      <div class="card theme-card booking-container__header px-md-4 px-3 py-3">
        <div
          v-if="providerId && bookingProvider"
          class="booking-container__title"
        >
          <span v-if="!loggedInUser">Welcome to </span>
          {{ textToUpperCase(bookingProvider.first) }}
          {{ textToUpperCase(bookingProvider.last) }} Booking Portal
        </div>
        <div v-else class="booking-container__title">
          <span v-if="!loggedInUser">Welcome to the </span>Service Booking
          Portal
        </div>
        <div class="booking-container__subtitle">
          Quickly and conveniently book the services you need in just two simple
          steps. First, select your session details: choose a treatment and a
          preferred date and time. Next, provide your personal information to
          complete the booking. Our streamlined interface ensures an effortless
          scheduling process, saving you time and hassle.
        </div>
      </div>
      <form @submit.prevent="verifyBookingRequestHelper" validate>
        <div class="card theme-card px-md-4 px-3 py-3 booking-container__body">
          <div v-show="activeStep === 1">
            <h1 class="mt-3 booking-container__title">Session Details</h1>
            <div class="col-12 mb-3">
              <label class="col-12 font-weight-bold py-2 pl-0 border-bottom">
                Select a treatment to book
              </label>
              <div class="d-flex flex-column">
                <alert
                  v-if="treatmentsLoading"
                  class="py-8-px light-shadow"
                ></alert>
                <alert
                  v-else-if="treatments.length === 0"
                  :hideLoader="true"
                  class="py-8-px light-shadow"
                >
                  No treatments available at the moment
                </alert>
                <div v-else class="treatments-scroll-container text-white">
                  <ul
                    v-if="!treatmentsLoading"
                    class="d-flex flex-column treatments-list"
                  >
                    <li
                      v-for="treatment in treatments"
                      :key="treatment.id"
                      class="treatment-card"
                      @click="dataToSubmit.treatment_id = treatment.id"
                      :class="{
                        active: dataToSubmit.treatment_id === treatment.id,
                      }"
                    >
                      <strong>
                        {{ treatment.name }}
                        ({{ treatment.readable_duration }})
                      </strong>
                      <span v-if="!isTreatmentFree(treatment.id)">
                        -
                        {{ treatment.readable_price }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div v-if="allowBookByProvider" class="d-flex flex-column mb-3">
              <div class="col-12">
                <label class="col-12 font-weight-bold py-2 pl-0 border-bottom">
                  Choose a healthcare
                  {{ $filters.providerLabel("singular", false) }}
                </label>
                <div
                  class="
                    custom-control custom-checkbox
                    d-flex
                    align-items-center
                    col-md-auto col-12
                    my-2
                    mb-3
                  "
                >
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="any-provider"
                    v-model="anyProvider"
                    :checked="anyProvider"
                  />
                  <label class="custom-control-label" for="any-provider">
                    Let the System Choose
                  </label>
                </div>
                <alert
                  v-if="anyProvider"
                  :hideLoader="true"
                  class="py-8-px light-shadow"
                >
                  The
                  {{ $clinicLabel({ type: "singular", capitalize: false }) }}
                  will automatically assign the most suitable
                  {{ $providerLabel({ type: "singular", capitalize: false }) }}
                  for your selected treatment. One of our experienced
                  professionals will confirm your booking to ensure you receive
                  the best possible care.
                </alert>
                <div v-else class="d-flex flex-column">
                  <alert
                    v-if="providersLoading"
                    class="py-8-px light-shadow"
                  ></alert>
                  <alert
                    v-else-if="!dataToSubmit.treatment_id"
                    :hideLoader="true"
                    class="py-8-px light-shadow"
                  >
                    Please select a treatment first to view available
                    {{ $filters.providerLabel("plural", false) }}
                  </alert>
                  <alert
                    v-else-if="providers.length === 0"
                    :hideLoader="true"
                    class="py-8-px light-shadow"
                  >
                    Currently, no
                    {{ $filters.providerLabel("plural", false) }} are available
                    for the selected treatment. You may choose a different
                    treatment or let the system find the best available
                    {{ $filters.providerLabel("singular", false) }} for you by
                    selecting &apos;Let the System Choose&apos;.
                  </alert>
                  <div v-else class="providers-scroll-container text-white">
                    <ul
                      v-if="!providersLoading"
                      class="d-flex flex-column providers-list"
                    >
                      <li
                        v-for="provider in providers"
                        :key="provider.id"
                        class="provider-card"
                        @click="dataToSubmit.provider_id = provider.id"
                        :class="{
                          active: dataToSubmit.provider_id === provider.id,
                        }"
                      >
                        <img
                          :src="provider.avatar || profileImagePlaceholder"
                          class="rounded-circle mr-3"
                          width="40"
                          height="40"
                          :alt="
                            $providerLabel({
                              type: 'singular',
                              capitalize: true,
                              suffix: ' Profile Picture',
                            })
                          "
                        />
                        <div>
                          <strong>{{ provider.name }}</strong>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div
                v-if="!isProviderSelectionValid && dataToSubmit.treatment_id"
                class="col-12 mt-2"
              >
                <div class="provider-selection-helper">
                  <i class="fas fa-info-circle mr-2"></i>
                  Please select either a health care provider or check 'Let the
                  System Choose' to specify a date and time and continue.
                </div>
              </div>
            </div>
            <div
              v-if="showAvailabilityCalendar"
              class="col-12 mb-3 d-flex flex-column"
              ref="bookingCalendarContainer"
            >
              <label class="col-12 font-weight-bold py-2 px-0 border-bottom">
                Select your preferred date and time
                <small class="text-muted d-block">
                  (Available dates are highlighted in green)
                </small>
              </label>
              <div class="d-flex flex-md-row flex-column">
                <div
                  class="
                    booking-calendar
                    col-md-6 col-12
                    pr-md-3
                    px-0
                    mb-md-0 mb-3
                  "
                >
                  <VueDatePicker
                    ref="calendar"
                    v-model="selectedDate"
                    @internal-model-change="onDateSelect($event)"
                    :timezone="timezone"
                    :six-weeks="true"
                    :start-date="calendarStartDate"
                    :time-config="{ enableTimePicker: false }"
                    :formats="{ input: 'yyyy-MM-dd' }"
                    :min-date="minDate"
                    :max-date="maxDate"
                    inline
                    auto-apply
                    :week-start="0"
                    :highlight="monthlyAvailableDates"
                    @update-month-year="onMonthYearChange"
                    :text-input="true"
                  />
                </div>
                <div class="d-flex flex-column col-12 col-md-6 px-md-3 px-0">
                  <div>
                    <label class="col-12 font-weight-bold pt-2 px-0">
                      Booking date
                    </label>
                    <div class="booking-date-data">
                      {{ formattedSelectedDate }}
                    </div>
                  </div>
                  <div class="mt-3">
                    <label
                      class="col-12 font-weight-bold px-0 d-flex flex-column"
                    >
                      Booking time
                      <small class="text-muted">
                        Eastern Time (US & Canada)
                      </small>
                    </label>
                    <div class="booking-date-data mb-3">
                      Only available times are shown
                    </div>
                    <div class="form-group d-flex flex-md-row flex-column mb-0">
                      <alert
                        v-if="
                          treatmentAvailabilityLoading ||
                          treatmentMonthlyAvailableDatesLoading
                        "
                        class="col-12 mb-0 time-dropdown"
                      ></alert>
                      <alert
                        v-else-if="availableTimes.length === 0"
                        :hideLoader="true"
                        class="col-12 mb-0 time-dropdown"
                      >
                        No available times
                      </alert>
                      <select
                        v-else
                        v-model="selectedTime"
                        class="form-control time-dropdown"
                      >
                        <option :value="null" selected>Select a time</option>
                        <option
                          v-for="option in availableTimes"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-show="activeStep === 2">
            <h1 class="mt-3 font-weight-bold">Personal Information</h1>
            <div
              v-if="!loggedInUser"
              class="mb-3 d-flex flex-md-row flex-column align-items-center"
            >
              <div class="mr-md-3 flex-grow-1 col-md-auto col-12 px-md-3 px-0">
                <label class="col-12 font-weight-bold pt-2 pl-0">
                  First Name
                </label>
                <input
                  v-model="dataToSubmit.first_name"
                  type="text"
                  class="form-control light-shadow"
                  required
                />
              </div>
              <div class="flex-grow-1 col-md-auto col-12 px-md-3 px-0">
                <label class="col-12 font-weight-bold pt-2 pl-0"
                  >Last Name</label
                >
                <input
                  v-model="dataToSubmit.last_name"
                  type="text"
                  class="form-control light-shadow"
                  required
                />
              </div>
            </div>
            <div v-if="!loggedInUser" class="col-12 mb-3 px-md-3 px-0">
              <label class="col-12 font-weight-bold pt-2 pl-0">Email</label>
              <input
                v-model="dataToSubmit.email"
                type="email"
                class="form-control light-shadow"
                required
              />
            </div>
            <div
              v-if="
                !loggedInUser &&
                !isTreatmentNonBillable(dataToSubmit.treatment_id)
              "
              class="col-12 mb-3 px-md-3 px-0"
            >
              <label class="col-12 font-weight-bold pt-2 pl-0"
                >Phone Number</label
              >
              <input
                v-model="dataToSubmit.phone_number"
                type="tel"
                class="form-control light-shadow"
                required
              />
            </div>
            <div
              v-if="
                !loggedInUser &&
                !isTreatmentNonBillable(dataToSubmit.treatment_id)
              "
              class="mb-3 d-flex flex-md-row flex-column align-items-center"
            >
              <div class="mr-md-3 flex-grow-1 col-md-auto col-12 px-md-3 px-0">
                <label class="col-12 font-weight-bold pt-2 pl-0">
                  Birth Date
                </label>
                <VueDatePicker
                  v-model="dataToSubmit.birth_date"
                  model-type="yyyy-MM-dd"
                  :formats="{ input: 'yyyy-MM-dd' }"
                  :time-config="{ enableTimePicker: false }"
                  auto-apply
                  :ui="{ input: 'form-control light-shadow' }"
                  placeholder="Select date"
                  :text-input="true"
                />
              </div>
              <div class="flex-grow-1 col-md-6 col-12 px-md-3 px-0">
                <label class="col-12 font-weight-bold pt-2 pl-0">Gender</label>
                <select
                  v-model="dataToSubmit.gender"
                  class="form-control"
                  required
                >
                  <option
                    v-for="option in genderOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
            </div>
            <div
              v-if="!isTreatmentNonBillable(dataToSubmit.treatment_id)"
              class="mb-3 d-flex flex-md-row flex-column align-items-center"
            >
              <div class="col-12 col-md-6 mb-3 px-md-3 px-0">
                <label class="col-12 font-weight-bold pt-2 pl-0">
                  Referred By Type
                </label>
                <select-control
                  v-model="dataToSubmit.referred_by_type"
                  :options="referredByTypeOptions"
                  labelClass="d-none"
                  wrap-class="mb-0"
                />
              </div>
              <div class="col-12 col-md-6 mb-3 px-md-3 px-0">
                <label class="col-12 font-weight-bold pt-2 pl-0">
                  Referred By Name
                </label>
                <input
                  :disabled="!dataToSubmit.referred_by_type"
                  v-model="dataToSubmit.referred_by_name"
                  type="text"
                  class="form-control"
                />
              </div>
            </div>
            <div
              v-if="
                !isTreatmentFree(dataToSubmit.treatment_id) &&
                !isTreatmentNonBillable(dataToSubmit.treatment_id)
              "
            >
              <div
                v-if="paymentMethodOptions.length > 1"
                class="col-12 mb-3 px-md-3 px-0"
              >
                <label class="col-12 font-weight-bold pt-2 pl-0">
                  Payment Method
                </label>
                <div
                  class="
                    custom-control custom-checkbox
                    d-flex
                    align-items-center
                    col-md-auto col-12
                    mb-3
                  "
                >
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="do-not-share-payment-method"
                    v-model="doNotSharePaymentMethod"
                    :checked="doNotSharePaymentMethod"
                  />
                  <label
                    class="custom-control-label"
                    for="do-not-share-payment-method"
                  >
                    Don&apos;t want to share the payment method
                  </label>
                </div>
                <select-control
                  v-if="!doNotSharePaymentMethod"
                  v-model="dataToSubmit.payment_method"
                  :options="paymentMethodOptions"
                  required
                  labelClass="d-none"
                />
              </div>
              <div
                v-if="dataToSubmit.payment_method === 'credit_card'"
                class="col-12 mb-3 px-md-3 px-0"
              >
                <label class="col-12 font-weight-bold pt-2 pl-0">
                  Card Information
                </label>
                <StripeCard />
              </div>
            </div>
            <div class="col-12 mb-3 px-md-3 px-0">
              <label class="col-12 font-weight-bold pt-2 pl-0">
                Additional Notes
              </label>
              <textarea
                v-model="dataToSubmit.additional_notes"
                class="form-control light-shadow"
                rows="3"
              ></textarea>
            </div>
          </div>
          <div class="col-12 d-flex justify-content-between">
            <button
              v-if="activeStep === 2"
              class="mb-3 btn btn-theme btn-md-lg d-flex align-items-center"
              @click.prevent="handleStep(-1)"
              type="button"
            >
              <i class="fas fa-angles-left display-6"></i>
              <span class="ml-2 display-6">Back</span>
            </button>
            <div class="w-100 d-flex justify-content-end">
              <button
                v-if="activeStep === 1"
                :class="{
                  'mb-3 btn btn-black btn-md-lg d-flex align-items-center': true,
                  disabled: nextButtonDisabled,
                }"
                :disabled="nextButtonDisabled"
                @click.prevent="handleStep(1)"
                type="button"
              >
                <span class="mr-2 display-6">Next</span>
                <i class="fas fa-angles-right display-6"></i>
              </button>
              <save
                :saving="verifyBookingRequestLoading"
                v-if="activeStep === 2"
                classes="btn btn-black mb-3 display-6 ml-3"
                type="submit"
              >
                Create Booking
              </save>
            </div>
          </div>
        </div>
        <BookingVerificationModal
          :loading="createBookingRequestLoading"
          v-model="showBookingVerificationModal"
          @verifyToken="createBookingHelper"
        />
      </form>
    </div>
  </div>
</template>
<script>
import BookingVerificationModal from "@/components/booking/BookingVerificationModal.vue";
import StripeCard from "@/components/stripe/StripeCard.vue";
import dateFormat from "@/utils/dateFormat";
import helpers from "@/utils/helpers";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";
dayjs.extend(utc);
dayjs.extend(timezone);

export default {
  name: "Booking",
  components: {
    BookingVerificationModal,
    StripeCard,
  },
  data() {
    return {
      showBookingVerificationModal: false,
      timezone: "America/Toronto",
      calendarStartDate: dayjs().tz(this.timezone).toISOString(),
      settingsLoading: true,
      profileImagePlaceholder: require("@/assets/img/avatars/profiles/avatar.jpg"),
      activeStep: 1,
      selectedDate: dayjs().tz(this.timezone).toISOString(),
      selectedTime: null,
      genderOptions: [
        { value: "0", label: "Other" },
        { value: "1", label: "Male" },
        { value: "2", label: "Female" },
      ],
      referredByTypeOptions: [
        { label: "Clinician", value: "clinician" },
        { label: "Third Party", value: "third_party" },
        { label: "Other", value: "other" },
      ],
      anyProvider: true,
      doNotSharePaymentMethod: false,
      dataToSubmit: {
        token: null,
        treatment_id: null,
        provider_id: null,
        start_time: null,
        end_time: null,
        first_name: null,
        last_name: null,
        email: null,
        phone_number: null,
        birth_date: null,
        gender: null,
        additional_notes: null,
        payment_method: "credit_card",
        payment_method_id: null,
        referred_by_type: "",
        referred_by_name: null,
      },
    };
  },
  async mounted() {
    await this.getPublicSettings();
    this.settingsLoading = false;

    if (this.providerId) {
      await this.getProvider(this.providerId);
      await this.getTreatments({
        providerId: this.providerId,
      });
      this.dataToSubmit.provider_id = this.providerId;
      this.dataToSubmit.from_provider_link = true;
    } else {
      await this.getTreatments();
    }

    if (!this.publicSettings?.STRIPE_ENABLED) {
      this.dataToSubmit.payment_method = "other";
    }
  },
  watch: {
    "dataToSubmit.treatment_id": function (newVal) {
      if (newVal) {
        this.resetSelectedTime();
        if (!this.providerId) {
          this.dataToSubmit.provider_id = null;
        }
        if (!this.anyProvider && this.allowBookByProvider) {
          this.getProviders(newVal);
        }
        if (this.isTreatmentFree(newVal)) {
          this.dataToSubmit.payment_method = "other";
        } else {
          this.dataToSubmit.payment_method = "credit_card";
        }

        this.getTreatmentAvailabilityHelper(
          newVal,
          this.selectedDate,
          this.selectedDate,
          this.dataToSubmit.provider_id
        );
        this.getTreatmentMonthlyAvailableDatesHelper(
          newVal,
          this.selectedDate,
          this.selectedDate,
          this.dataToSubmit.provider_id
        );
      }
    },
    "dataToSubmit.provider_id": function (newVal) {
      if (newVal && this.dataToSubmit.treatment_id) {
        this.resetSelectedTime();
        this.getTreatmentAvailabilityHelper(
          this.dataToSubmit.treatment_id,
          this.selectedDate,
          this.selectedDate,
          newVal
        );
        this.getTreatmentMonthlyAvailableDatesHelper(
          this.dataToSubmit.treatment_id,
          this.selectedDate,
          this.selectedDate,
          newVal
        );
      }
    },
    anyProvider: function (newVal) {
      if (newVal) {
        const isProviderSelected = this.dataToSubmit.provider_id !== null;
        this.resetSelectedTime();
        this.dataToSubmit.provider_id = null;
        if (this.dataToSubmit.treatment_id && isProviderSelected) {
          this.getTreatmentAvailabilityHelper(
            this.dataToSubmit.treatment_id,
            this.selectedDate,
            this.selectedDate
          );
          this.getTreatmentMonthlyAvailableDatesHelper(
            this.dataToSubmit.treatment_id,
            this.selectedDate,
            this.selectedDate
          );
        }
      } else if (this.dataToSubmit.treatment_id) {
        this.resetSelectedTime();
        this.dataToSubmit.provider_id = null;
        this.getProviders(this.dataToSubmit.treatment_id);
      }
    },
    doNotSharePaymentMethod: function (newVal) {
      if (newVal) {
        this.dataToSubmit.payment_method = "other";
      } else {
        this.dataToSubmit.payment_method = "credit_card";
      }
    },
  },
  computed: {
    ...mapState({
      loggedInUser: (state) => state.auth.user,
      publicSettings: (state) => state.settings.publicSettings.data,
      publicSettingsLoading: (state) => state.settings.publicSettings.loading,
      treatments: (state) => state.booking.treatments.data,
      treatmentsLoading: (state) => state.booking.treatments.loading,
      providers: (state) => state.booking.providers.data,
      providersLoading: (state) => state.booking.providers.loading,
      treatmentAvailability: (state) =>
        state.booking.treatmentAvailability.data,
      treatmentAvailabilityLoading: (state) =>
        state.booking.treatmentAvailability.loading,
      treatmentMonthlyAvailableDates: (state) =>
        state.booking.treatmentMonthlyAvailableDates.data.dates,
      treatmentMonthlyAvailableDatesLoading: (state) =>
        state.booking.treatmentMonthlyAvailableDates.loading,
      verifyBookingRequestLoading: (state) =>
        state.booking.verifyBookingRequestLoading,
      createBookingRequestLoading: (state) =>
        state.booking.createBookingRequestLoading,
      bookingProviderLoading: (state) => state.booking.provider.loading,
      bookingProvider: (state) => state.booking.provider.data,
    }),
    showAvailabilityCalendar: function () {
      if (!this.dataToSubmit.treatment_id) {
        return false;
      }
      // If book by provider is not allowed, just need treatment
      if (!this.allowBookByProvider) {
        return true;
      }
      return this.anyProvider || this.dataToSubmit.provider_id;
    },
    isProviderSelectionValid: function () {
      // Returns true if provider selection requirements are met
      if (!this.allowBookByProvider) {
        return true;
      }
      return this.anyProvider || this.dataToSubmit.provider_id;
    },
    nextButtonDisabled: function () {
      return (
        !this.dataToSubmit.treatment_id ||
        !this.selectedDate ||
        !this.selectedTime ||
        (!this.anyProvider &&
          !this.dataToSubmit.provider_id &&
          this.allowBookByProvider)
      );
    },
    formattedSelectedDate: function () {
      return this.selectedDate
        ? this.parseDate(this.selectedDate, "MMMM DD, YYYY")
        : "Please select a date";
    },
    availableTimes() {
      if (this.treatmentAvailability?.slots) {
        return this.treatmentAvailability.slots
          .filter((slot) => {
            return this.isDateAfterToday(slot.start);
          })
          .map((slot) => {
            return {
              label: this.parseBackendDate(slot.start).format("h:mm A"),
              value: slot.start,
            };
          });
      }
      return [];
    },
    userAvatar: function () {
      return this.loggedInUser?.profile_picture || this.profileImagePlaceholder;
    },
    onlineBookingEnabled: function () {
      return this.publicSettings?.ONLINE_BOOKING_ENABLED;
    },
    allowBookByProvider: function () {
      return this.publicSettings?.BOOK_BY_PROVIDER && !this.providerId;
    },
    minDate: function () {
      return dayjs().toDate();
    },
    maxDate: function () {
      return dayjs().add(90, "day").endOf("month").toDate();
    },
    paymentMethodOptions: function () {
      let options = [
        { label: "Credit Card", value: "credit_card" },
        { label: "Secure Payment Link", value: "secure_payment_link" },
        { label: "Email Money Transfer", value: "email_money_transfer" },
        { label: "Other", value: "other" },
      ];
      if (!this.publicSettings?.STRIPE_ENABLED) {
        options = options.filter(
          (option) =>
            option.value !== "credit_card" &&
            option.value !== "secure_payment_link"
        );
      }

      if (!this.publicSettings?.ALLOW_EMAIL_MONEY_TRANSFER) {
        options = options.filter(
          (option) => option.value !== "email_money_transfer"
        );
      }

      return options;
    },
    providerId: function () {
      return this.$route.params.providerId;
    },
    monthlyAvailableDates: function () {
      const dates = this.treatmentMonthlyAvailableDates
        .filter((date) => {
          return this.isDateAfterToday(
            this.parseDate(date).endOf("day").format()
          );
        })
        .map((date) => {
          return this.parseBackendDate(date).toISOString();
        });
      return { dates };
    },
    bookingSummary: function () {
      const treatment = this.treatments.find(
        (t) => t.id === this.dataToSubmit.treatment_id
      );
      const provider = this.providers.find(
        (p) => p.id === this.dataToSubmit.provider_id
      );

      const treatmentName = treatment ? treatment.name : "N/A";
      const treatmentDuration = treatment ? treatment.readable_duration : "";
      const providerName =
        this.anyProvider || !provider
          ? `System will assign the ${this.$providerLabel({
              type: "singular",
              capitalize: false,
            })}`
          : provider.name;
      const bookingDate = this.parseDate(this.selectedDate, "MMMM DD, YYYY");
      const bookingTime = this.selectedTime
        ? this.parseBackendDate(this.selectedTime).format("h:mm A")
        : "N/A";

      return `
        <div style="text-align: left; margin-top: 20px;">
          <p><strong>Treatment:</strong> ${treatmentName} ${
        treatmentDuration ? `(${treatmentDuration})` : ""
      }</p>
          <p><strong>${this.$providerLabel({
            type: "singular",
            capitalize: true,
          })}:</strong> ${providerName}</p>
          <p><strong>Date:</strong> ${bookingDate}</p>
          <p><strong>Time:</strong> ${bookingTime}</p>
        </div>
      `;
    },
  },
  methods: {
    ...mapActions({
      getProvider: "booking/getProvider",
      getTreatments: "booking/getTreatments",
      getProviders: "booking/getProviders",
      getPublicSettings: "settings/getPublicSettings",
      getTreatmentAvailability: "booking/getTreatmentAvailability",
      getTreatmentMonthlyAvailableDates:
        "booking/getTreatmentMonthlyAvailableDates",
      verifyBookingRequest: "booking/verifyBookingRequest",
      createBookingRequest: "booking/createBookingRequest",
    }),
    textToUpperCase: function (text) {
      if (!text) {
        return "";
      }
      return helpers.textToUpperCase(text);
    },
    handleStep: function (step) {
      this.activeStep += step;
    },
    setStep: function (value) {
      this.activeStep = value;
    },
    onDateSelect: function (date) {
      if (!date) return;
      const selectedDate = this.parseDate(this.selectedDate, "YYYY-MM-DD");
      const dayDate = this.parseDate(date, "YYYY-MM-DD");
      if (selectedDate === dayDate) {
        return;
      }

      this.selectedDate = this.parseDate(date).toISOString();
      this.selectedTime = null;
      this.getTreatmentAvailabilityHelper(
        this.dataToSubmit.treatment_id,
        this.selectedDate,
        this.selectedDate,
        this.dataToSubmit.provider_id
      );
    },
    onMonthYearChange: function ({ month, year }) {
      const startDateOfTheMonth = dayjs(`${year}-${month + 1}-01`).startOf(
        "month"
      );
      const endDateOfTheMonth = dayjs(`${year}-${month + 1}-01`).endOf("month");
      this.getTreatmentMonthlyAvailableDatesHelper(
        this.dataToSubmit.treatment_id,
        startDateOfTheMonth,
        endDateOfTheMonth,
        this.dataToSubmit.provider_id
      );
    },
    getTreatmentAvailabilityHelper: function (
      treatmentId,
      startDate,
      endDate,
      providerId = null
    ) {
      const startDateTime = `${this.parseDate(
        startDate,
        "YYYY-MM-DD"
      )}T00:00:00.000000Z`;
      const endDateTime = `${this.parseDate(
        endDate,
        "YYYY-MM-DD"
      )}T23:59:59.000000Z`;
      const payloadStart = dayjs(startDateTime).utc().format();
      const payloadEnd = dayjs(endDateTime).utc().format();

      this.getTreatmentAvailability({
        treatmentId,
        start: payloadStart,
        end: payloadEnd,
        providerId,
      });
    },
    getTreatmentMonthlyAvailableDatesHelper: function (
      treatmentId,
      startDate,
      endDate,
      providerId = null
    ) {
      const startDateTimeOfTheMonth = dayjs(startDate)
        .startOf("month")
        .format("YYYY-MM-DD");
      const endDateTimeOfTheMonth = dayjs(endDate)
        .endOf("month")
        .format("YYYY-MM-DD");
      const startDateTime = `${dateFormat(
        startDateTimeOfTheMonth,
        "YYYY-MM-DD"
      )}T00:00:00.000000Z`;
      const endDateTime = `${dateFormat(
        endDateTimeOfTheMonth,
        "YYYY-MM-DD"
      )}T23:59:59.000000Z`;
      const payloadStart = dayjs(startDateTime).utc().format();
      const payloadEnd = dayjs(endDateTime).utc().format();

      this.getTreatmentMonthlyAvailableDates({
        treatmentId,
        start: payloadStart,
        end: payloadEnd,
        providerId,
      });
    },
    verifyBookingRequestHelper: function () {
      if (this.dataToSubmit?.payment_method === "credit_card") {
        const stripElement = document.querySelector(
          ".booking-container #card-element"
        );
        if (
          stripElement &&
          !stripElement.classList.contains("StripeElement--complete")
        ) {
          Swal.fire({
            title: "Invalid Payment Method",
            html: `Please enter a valid and complete card information.`,
            icon: "error",
            confirmButtonText: "OK",
          });
          return;
        }
      }

      this.verifyBookingRequest({
        first_name: this.loggedInUser
          ? this.loggedInUser.first
          : this.dataToSubmit.first_name,
        email: this.loggedInUser
          ? this.loggedInUser.email
          : this.dataToSubmit.email,
        phone_number: this.loggedInUser
          ? this.loggedInUser.phone_number
          : this.dataToSubmit.phone_number,
        treatment_id: this.dataToSubmit.treatment_id,
      }).then((response) => {
        if (response) {
          this.showBookingVerificationModal = true;
        }
      });
    },
    getBookingTimes: function () {
      const treatment = this.treatments.find(
        (treatment) => treatment.id === this.dataToSubmit.treatment_id
      );
      this.dataToSubmit.start_time = `${dateFormat(
        this.selectedDate,
        "YYYY-MM-DD"
      )}T${dayjs(this.selectedTime).format("HH:mm:ss")}.000000Z`;
      this.dataToSubmit.start_time = dayjs(
        this.dataToSubmit.start_time
      ).format();
      this.dataToSubmit.end_time = `${dateFormat(
        this.selectedDate,
        "YYYY-MM-DD"
      )}T${dayjs(this.selectedTime)
        .add(treatment.duration, "minute")
        .format("HH:mm:ss")}.000000Z`;
      this.dataToSubmit.end_time = dayjs(this.dataToSubmit.end_time).format();

      const startDateTime = dayjs(this.dataToSubmit.start_time).utc().format();
      const endDateTime = dayjs(this.dataToSubmit.end_time).utc().format();

      return { start_time: startDateTime, end_time: endDateTime };
    },
    goBack() {
      window.history.go(-1);
    },
    createBookingHelper: async function (token) {
      if (!token) {
        Swal.fire({
          title: "Invalid Token",
          html: `The token you entered is invalid. Please try again.`,
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      if (this.nextButtonDisabled) {
        Swal.fire({
          title: "Invalid Booking",
          html: `Please fill in all the required fields.`,
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      const { start_time, end_time } = this.getBookingTimes();
      this.dataToSubmit = {
        ...this.dataToSubmit,
        email: this.loggedInUser
          ? this.loggedInUser.email
          : this.dataToSubmit.email,
        birth_date: this.dataToSubmit.birth_date
          ? `${dateFormat(
              this.dataToSubmit.birth_date,
              "YYYY-MM-DD"
            )}T00:00:00.000000Z`
          : null,
        is_logged_in: this.loggedInUser ? 1 : 0,
      };
      let payment = undefined;
      if (this.dataToSubmit.payment_method === "credit_card") {
        payment = await window.stripe.createPaymentMethod({
          type: "card",
          card: window.cardElement,
        });
        this.dataToSubmit.payment_method_id = payment?.paymentMethod?.id;
      }

      this.createBookingRequest({
        ...this.dataToSubmit,
        start_time,
        end_time,
        token,
      }).then((response) => {
        if (response) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: "appointment_booked" });

          Swal.fire({
            title: "Booking Successful",
            html: `
              <p>Your booking has been successfully created. You will receive an email confirmation shortly.</p>
              ${this.bookingSummary}
            `,
            icon: "success",
            confirmButtonText: "OK",
            width: "600px",
          }).then(() => {
            this.$router.push({ name: "login" });
          });
        }
      });
    },
    moveToDate: function (date) {
      this.selectedDate = dayjs(date).tz(this.timezone).toISOString();
      // Set `calendarStartDate` so the datepicker navigates to the provided month/year.
      this.calendarStartDate = dayjs(date).toISOString();
    },
    resetSelectedTime: function () {
      this.moveToDate(new Date());
      this.selectedTime = null;
    },
    isTreatmentNonBillable: function (treatmentId) {
      const treatment = this.treatments.find(
        (treatment) => treatment.id === treatmentId
      );
      if (!treatment) {
        return false;
      }
      return treatment?.service?.is_non_billable;
    },
    isTreatmentFree: function (treatmentId) {
      const treatment = this.treatments.find(
        (treatment) => treatment.id === treatmentId
      );
      if (!treatment) {
        return false;
      }
      return (
        !treatment.price ||
        treatment.price === 0 ||
        treatment.price === "$0.00" ||
        treatment?.service?.is_non_billable
      );
    },
    parseDate: function (date, format = null) {
      if (!format) {
        return dayjs(date).tz(this.timezone);
      }
      return dayjs(date).tz(this.timezone).format(format);
    },
    parseBackendDate: function (date) {
      return dayjs.tz(date, this.timezone).tz(this.timezone);
    },
    isDateAfterToday: function (date) {
      return dayjs
        .tz(date, this.timezone)
        .isAfter(dayjs().tz(this.timezone).format("YYYY-MM-DD HH:mm:ss"));
    },
  },
};
</script>

<style lang="scss">
.booking-calendar {
  .dp__outer_menu_wrap,
  .dp__menu,
  .dp__calendar,
  .dp__calendar_wrap {
    width: 100% !important;
    max-width: 100% !important;
  }
  .dp__calendar_header,
  .dp__calendar_row {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr) !important;
  }
  .dp__calendar_header_item {
    width: 100% !important;
  }

  .dp__cell_inner {
    &.dp__cell_highlight {
      font-weight: 700;
      color: #22543d;
      background-color: #c6f6d5;
    }
  }
}
</style>

<style lang="scss" scoped>
.booking-container {
  display: flex;
  flex-flow: column;
  gap: 5px;
  &__header {
    background-color: #f5f5f5;
    display: flex;
    flex-flow: column;
    gap: 5px;
  }
  &__title {
    font-size: clamp(1.3rem, 2vw, 1.5rem);
    font-weight: bold;
  }
  &__subtitle {
    font-size: 16px;
  }
  &__body {
    display: flex;
    flex-flow: column;
    gap: 15px;
  }

  &__user-name {
    font-size: clamp(1.1rem, 2vw, 1.5rem);
    font-weight: bold;
  }

  label {
    font-size: 17px;
  }
  .display-6 {
    font-size: 1.1rem;
    font-weight: 600;
  }

  .treatments-scroll-container,
  .providers-scroll-container {
    max-height: 273px;
    transition: all ease 1000ms;
    overflow-y: auto;
    border-radius: 0 0 4px 4px;
    .treatments-list,
    .providers-list {
      display: flex;
      flex-flow: column;
      gap: 10px;
      list-style-type: none;
      padding: 0;

      .treatment-card,
      .provider-card {
        font-size: clamp(0.8rem, 2vw, 1rem);
        background-color: rgba(37, 37, 37);
        padding: 10px 15px;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.1s;
        &:hover {
          background-color: rgba(37, 37, 37, 0.8);
        }
        &.active {
          background-color: var(--theme-color);
          color: var(--main-text-color);
        }
      }
      .provider-card {
        display: flex;
        align-items: center;
      }
    }
  }

  .booking-date-data {
    padding: 10px 15px;
    background-color: rgba(37, 37, 37);
    border-radius: 4px;
    color: white;
    font-size: clamp(1rem, 2vw, 1.1rem);
    text-align: center;
  }
  .no-time-available {
    padding: 10px 15px;
    background-color: var(--theme-color);
    border-radius: 4px;
    color: black;
    font-size: clamp(1rem, 2vw, 1.1rem);
    text-align: center;
  }
  .time-dropdown {
    padding: 10px 15px;
    font-size: clamp(1rem, 2vw, 1.1rem);
  }

  .provider-selection-helper {
    padding: 12px 15px;
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    color: #856404;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
  }
}
</style>
